import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useHighlightedBrands } from 'api/queries/useHighlightedBrands'
import { ReactComponent as CaretRightIcon } from 'assets/icons/CaretRight.svg'
import EmptyLogo from 'assets/images/card-placeholder.png'
import { buildBvaDetailsPath } from 'utils/bvaSlug'

import './ExploreMoreBvas.scss'

interface Props {
  currentBrandId?: string
}

const SCROLL_STEP = 227 // card width (211px) + gap (16px)

export const ExploreMoreBvas = ({ currentBrandId }: Props) => {
  const { data: brands, isLoading, isError } = useHighlightedBrands()
  const navigate = useNavigate()
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const otherBrands = (brands ?? []).filter(brand => brand.brandId !== currentBrandId)

  const updateNavState = () => {
    const track = trackRef.current
    if (!track) return
    setIsBeginning(track.scrollLeft <= 0)
    setIsEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1)
  }

  useEffect(() => {
    updateNavState()

    const track = trackRef.current
    if (!track) return

    const resizeObserver = new ResizeObserver(updateNavState)
    resizeObserver.observe(track)

    return () => resizeObserver.disconnect()
  }, [otherBrands.length])

  if (isLoading || isError || otherBrands.length === 0) {
    return null
  }

  const scrollByOffset = (offset: number) => {
    trackRef.current?.scrollBy({ left: offset, behavior: 'smooth' })
  }

  return (
    <div className="explore-more-bvas">
      <div className="explore-more-bvas__header">
        <p className="explore-more-bvas__title headline-s">Explore more BVAs</p>
        <div className="explore-more-bvas__nav">
          <button
            type="button"
            aria-label="Previous"
            className={`explore-more-bvas__button explore-more-bvas__button--prev ${isBeginning ? 'explore-more-bvas__button--disabled' : ''}`}
            disabled={isBeginning}
            onClick={() => scrollByOffset(-SCROLL_STEP)}
          >
            <CaretRightIcon />
          </button>
          <button
            type="button"
            aria-label="Next"
            className={`explore-more-bvas__button explore-more-bvas__button--next ${isEnd ? 'explore-more-bvas__button--disabled' : ''}`}
            disabled={isEnd}
            onClick={() => scrollByOffset(SCROLL_STEP)}
          >
            <CaretRightIcon />
          </button>
        </div>
      </div>
      <div className="explore-more-bvas__track" ref={trackRef} onScroll={updateNavState}>
        {otherBrands.map(brand => (
          <div
            key={brand.id}
            className="explore-more-bvas__card"
            onClick={() => navigate(buildBvaDetailsPath(brand.id, brand.brandName))}
          >
            <img
              src={brand.logoUrl ? brand.logoUrl : brand.brandLogoUrl ? brand.brandLogoUrl : EmptyLogo}
              alt={brand.brandName}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
